// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import EventEmitter from 'events';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { LocaleNames, Resources } from '@shared/localizations/Resources';
import { AlertAfterExecute, AlertCondition, AlertImportance, AlertType, getAlertAfterExecuteKey, getAlertConditionKey, getAlertImportanceKey, getAlertTypeKey } from '@shared/utils/Alert/AlertConstants';
import { messageBoxHandler } from '@shared/utils/AppHandlers';
import { NewContextMenuItemType, createContextMenuItemsFromEnum } from '@shared/utils/ContextMenuUtils';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Enum } from '@shared/utils/Enum';
import { DisplayMode, getDisplayModeKey } from '@shared/utils/Enums/DisplayMode';
import { ProductType, getProductTypeKey } from '@shared/utils/Instruments/ProductType';
import { WatchlistSheetItem } from '@shared/utils/Managers/WatchlistManager/WatchlistSheetItem';
import { WatchlistSheets, _WatchlistSheets } from '@shared/utils/Managers/WatchlistManager/WatchlistSheets';
import { NumericUtils } from '@shared/utils/NumericUtils';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { ThemePreference } from '@shared/utils/ThemesEnum';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { TimeZoneInfo } from '@shared/utils/Time/TimeZoneInfo';
import { OffsetModeViewEnum, getOffsetModeViewEnumKey } from '@shared/utils/Trading/OffsetModeViewEnum';
import { TIF } from '@shared/utils/Trading/OrderTif';
import { OrderTif, getOrderTifKey } from '@shared/utils/Trading/OrderTifEnum';
import { OrderType, OrderTypeMap, getOrderTypeKey } from '@shared/utils/Trading/OrderType';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { TradingUtils } from '@shared/utils/Trading/TradingUtils';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { AccountFollowerService } from './AccountFollowerService';
import { ApplicationInfo } from './ApplicationInfo';
import { ApplicationVersion } from './ApplicationVersion';
import { HotkeysManager } from './cache/Hotkeys/HotkeysManager';
import { InsDefSettings } from './cache/InstrumentDefaults';
import { SLTPTriggerUtils } from './cache/OrderParams/SLTPTriggerUtils';
import { Connection } from './Connection';
import { DataCache } from './DataCache';
import { BoolNumeric, DynProperty, DynPropertyRelationType } from './DynProperty';
import { IsAllowed } from './IsAllowed';
import { SessionSettingsConst } from './SessionSettingsConst';
import { BaseSettings } from './Settings/BaseGeneralSettingsWrapper';
import { SettingsLoggingManager } from './SettingsLoggingManager';
import { BaseSettingsUtils } from './UtilsClasses/BaseGeneralSettingsUtilsWrapper';
import { TerceraLineStyleComboBoxUtils } from './UtilsClasses/TerceraTIFComboBoxUtils';
class _SessionSettings {
    constructor() {
        this.chartSheets = {};
        this.savedOrdersSheets = {};
        this.timeZoneChanged = new CustomEvent();
        this.sheetsChangedEvents = {
            chartSheets: new CustomEvent(),
            savedOrdersSheets: new CustomEvent()
        };
        this.AdditionalPropertiesHandlers = [];
        this.AdditionalCallBacksHandlers = [];
        this._eventEmitter = new EventEmitter();
        this.AdditionalPropertiesHandlers.push(this.GetAlertPanelProperties.bind(this, true));
        TradingLockUtils.TradingLock.TradingLockChanged.Subscribe(this.save, this);
    }
    GetAllowedForOrderType(orderType) {
        let isAllowedByRoutes = false;
        const tradeRoutes = DataCache.Routes.getTradeRoutes();
        for (const route of tradeRoutes) {
            if (!isNullOrUndefined(route)) {
                const allowedOrderType = route.IsAllowableOrderType(orderType);
                isAllowedByRoutes = isAllowedByRoutes || allowedOrderType;
            }
        }
        if (orderType === OrderType.Manual) {
            return DataCache.isAllowedForMyUser(RulesSet.FUNCTION_POSITIONSADMIN);
        }
        if (orderType === OrderType.TrailingStop) {
            return DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRAILING_STOP);
        }
        if (orderType === OrderType.Care) {
            const isAllowedByRule = DataCache.isAllowedForMyUser(RulesSet.FUNCTION_CARE_ORDERS);
            return isAllowedByRoutes && isAllowedByRule;
        }
        return isAllowedByRoutes;
    }
    GetAllowedTifsForOrderTypes(orderTypeArray) {
        const allowedTifsSet = new Set();
        const tifIds = Enum.GetEnumValues(OrderTif);
        for (const route of DataCache.Routes.getTradeRoutes()) {
            for (const orderType of orderTypeArray) {
                if (!route.IsAllowableOrderType(orderType))
                    continue;
                for (const tifId of tifIds) {
                    if (route.IsAllowableTif(tifId, orderType)) {
                        allowedTifsSet.add(tifId);
                    }
                }
            }
        }
        return Array.from(allowedTifsSet);
    }
    Properties(forSaving = false) {
        let properties = [];
        for (let i = 0; i < this.AdditionalPropertiesHandlers.length; i++) {
            properties = properties.concat(this.AdditionalPropertiesHandlers[i]());
        }
        let sortIndex = 0;
        let dp = new DynProperty('property.Language', BaseSettings.language, DynProperty.COMBOBOX, DynProperty.PARAM_GROUP);
        dp.objectVariants = [];
        for (let i = 0; i < LocaleNames.length; i++) {
            const localeName = LocaleNames[i];
            if (!Resources.isHidden('locale.' + localeName)) {
                dp.objectVariants.push({
                    type: NewContextMenuItemType.Default,
                    value: localeName,
                    text: Resources.getLangNameByLocale(localeName)
                });
            }
        }
        dp.sortIndex = sortIndex++;
        properties.push(dp);
        dp = new DynProperty('ribbon.enviroment.style', BaseSettings.currentTheme, DynProperty.COMBOBOX, DynProperty.PARAM_GROUP);
        dp.objectVariants = [];
        for (const name of ThemeNames) {
            const localKey = `ribbon.theme.${name}`;
            if (!Resources.isHidden(localKey)) {
                dp.objectVariants.push({
                    type: NewContextMenuItemType.Default,
                    value: name,
                    text: localKey
                });
            }
        }
        dp.sortIndex = sortIndex++;
        properties.push(dp);
        dp = new DynProperty('property.displayMode', BaseSettings.displayMode, DynProperty.COMBOBOX, DynProperty.PARAM_GROUP);
        dp.objectVariants = createContextMenuItemsFromEnum(DisplayMode, getDisplayModeKey);
        dp.sortIndex = sortIndex++;
        properties.push(dp);
        dp = new DynProperty('property.onboardinFirstTimeShowed', BaseSettings.onboardInFirstTimeShowed, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = sortIndex++;
        properties.push(dp);
        dp = new DynProperty('property.DisableInactivityPeriod', BaseSettings.disableInactivityPeriod, DynProperty.BOOLEAN_EVT, Resources.isHidden('property.DisableInactivityPeriod') ? DynProperty.HIDDEN_GROUP : DynProperty.PARAM_GROUP);
        dp.sortIndex = sortIndex++;
        // dp.tooltip = Resources.getResource("property.DisableInactivityPeriod.tt")
        dp.BeforeChangeEvent = function (newCheck, resolver) {
            if (!newCheck) {
                return resolver(false);
            }
            const scr = messageBoxHandler.Show(Resources.getResource('property.general.DisableInactivityPeriod.confirmation.caption'), Resources.getResource('property.general.DisableInactivityPeriod.confirmation.message'), messageBoxHandler.msgType.Info);
            scr.OK_Handler.then(function () {
                resolver(true);
            });
            scr.NO_Handler.then(function () {
                resolver(false);
            });
        };
        properties.push(dp);
        let prop = new DynProperty('property.Enable Sounds', BaseSettings.enableSounds, DynProperty.BOOLEAN, DynProperty.PARAM_GROUP);
        prop.sortIndex = sortIndex++;
        prop.assignedProperty = ['property.DisableStartEndSounds'];
        properties.push(prop);
        prop = new DynProperty('property.DisableStartEndSounds', BaseSettings.disableStartEndSound, DynProperty.BOOLEAN, Resources.isHidden('property.DisableStartEndSounds') ? DynProperty.HIDDEN_GROUP : DynProperty.PARAM_GROUP);
        prop.sortIndex = sortIndex++;
        properties.push(prop);
        prop = new DynProperty(SessionSettingsConst.DOUBLE_CLICK_AVAILABILITY_FOR_POSITIONS_PANEL, IsAllowed.PositionsPanelDoubleClickAvailability, DynProperty.BOOLEAN, Resources.isHidden(SessionSettingsConst.DOUBLE_CLICK_AVAILABILITY_FOR_POSITIONS_PANEL) || Resources.isHidden('panel.positions.doubleClickAvailability')
            ? DynProperty.HIDDEN_GROUP
            : DynProperty.PARAM_GROUP);
        prop.sortIndex = sortIndex++;
        prop.tooltip = Resources.getResource(SessionSettingsConst.DOUBLE_CLICK_AVAILABILITY_FOR_POSITIONS_PANEL_TOOLTIP);
        properties.push(prop);
        prop = new DynProperty(SessionSettingsConst.DOUBLE_CLICK_AVAILABILITY_FOR_ORDERS_PANEL, IsAllowed.OrdersPanelDoubleClickAvailability, DynProperty.BOOLEAN, Resources.isHidden(SessionSettingsConst.DOUBLE_CLICK_AVAILABILITY_FOR_ORDERS_PANEL) || Resources.isHidden('panel.orders.doubleClickAvailability')
            ? DynProperty.HIDDEN_GROUP
            : DynProperty.PARAM_GROUP);
        prop.sortIndex = sortIndex++;
        prop.tooltip = Resources.getResource(SessionSettingsConst.DOUBLE_CLICK_AVAILABILITY_FOR_ORDERS_PANEL_TOOLTIP);
        properties.push(prop);
        dp = new DynProperty(SessionSettingsConst.IS_ROUNDED_AVERAGE_OPEN_PRICE, BaseSettings.roundedAverageOpenPrice, DynProperty.BOOLEAN, DynProperty.VIEW_GROUP);
        dp.sortIndex = 1;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.DISPLAY_QUANTITY_IN_LOTS, BaseSettings.isQuantityInLots, DynProperty.BOOLEAN, Resources.isHidden(SessionSettingsConst.DISPLAY_QUANTITY_IN_LOTS)
            ? DynProperty.HIDDEN_GROUP
            : DynProperty.VIEW_GROUP);
        dp.sortIndex = 2;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.REVERSE_BUTTONS_ORDER, BaseSettings.reverseButtonsOrder, DynProperty.BOOLEAN, DynProperty.VIEW_GROUP);
        dp.sortIndex = 3;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.ABBREVIATE_VOLUMES, BaseSettings.abbreviateVolumes, DynProperty.BOOLEAN, DynProperty.VIEW_GROUP);
        dp.sortIndex = 4;
        dp.tooltip = Resources.getResource('property.abbreviateVolumes.ToolTip');
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.TOOLTIPS, BaseSettings.tooltips, DynProperty.BOOLEAN, DynProperty.VIEW_GROUP);
        dp.sortIndex = 7;
        properties.push(dp);
        dp = new DynProperty('property.logSnapshotHasBeenSent', BaseSettings.logSnapshotHasBeenSent, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP);
        properties.push(dp);
        dp = new DynProperty('property.isCashModePreferred', BaseSettings.isCashModePreferred, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP);
        properties.push(dp);
        // #region Sounds
        // #endregion
        dp = new DynProperty('property.routing.account', BaseSettings.tradingAccountID, DynProperty.ACCOUNT, DataCache.getNumberOfAccounts() === 1 ? DynProperty.HIDDEN_GROUP : DynProperty.TRADING_DEFAULTS_GROUP);
        dp.sortIndex = 0;
        properties.push(dp);
        dp = new DynProperty('property.general.defaultSymbol', BaseSettings.tradingSymbolID, DynProperty.INSTRUMENT, DynProperty.TRADING_DEFAULTS_GROUP);
        dp.sortIndex = 10;
        properties.push(dp);
        const dc = DataCache;
        const sltpSettingsHideByRules = IsAllowed.IsProtectiveOrders(); // || !DataCache.isAllowed(RulesSet.FUNCTION_SLTP)
        dp = new DynProperty(SessionSettingsConst.SET_SLTP_VALUES_IN_OFFSET, BaseSettings.isSlTpInOffset, DynProperty.BOOLEAN, // isHidden ключ ('property.sltpOffsetMode') отличается от DynProp.name('sltpOffsetMode'), стоило б исправить, но вдруг уже настроено так у кого-то из клиентов потому оставил
        Resources.isHidden('property.' + SessionSettingsConst.SET_SLTP_VALUES_IN_OFFSET) || sltpSettingsHideByRules
            ? DynProperty.HIDDEN_GROUP
            : DynProperty.TRADING_DEFAULTS_GROUP);
        dp.sortIndex = 30;
        properties.push(dp);
        // isHidden ключ ('property.property.OffsetMode') отличается от DynProp.name('property.OffsetMode'), стоило б исправить, но вдруг уже настроено так у кого-то из клиентов потому оставил
        const offsetModeGroup = Resources.isHidden(SessionSettingsConst.SHOW_OFFSET_IN) ||
            Resources.isHidden('property.' + SessionSettingsConst.SHOW_OFFSET_IN) ||
            sltpSettingsHideByRules
            ? DynProperty.HIDDEN_GROUP
            : DynProperty.TRADING_DEFAULTS_GROUP;
        dp = new DynProperty(SessionSettingsConst.SHOW_OFFSET_IN, this.getAvailableOffsetMode(BaseSettings.offsetType), DynProperty.COMBOBOX, offsetModeGroup);
        dp.assignedProperty = [SessionSettingsConst.USE_STOP_LIMIT_INSTEAD_OF_STOP];
        dp.DynPropertyRelationType = DynPropertyRelationType.Visibility;
        dp.sortIndex = 40;
        dp.objectVariants = createContextMenuItemsFromEnum(OffsetModeViewEnum, getOffsetModeViewEnumKey, (value) => this.isOffsetModeAvailable(value));
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.USE_STOP_LIMIT_INSTEAD_OF_STOP, BaseSettings.useStopLimitInsteadStop, DynProperty.BOOLEAN, Resources.isHidden(SessionSettingsConst.USE_STOP_LIMIT_INSTEAD_OF_STOP)
            ? DynProperty.HIDDEN_GROUP
            : DynProperty.TRADING_DEFAULTS_GROUP);
        dp.enabilityValue = [
            OffsetModeViewEnum.Ticks,
            OffsetModeViewEnum.TicksFractionalForForex,
            OffsetModeViewEnum.Points
        ];
        dp.sortIndex = 45;
        dp.assignedProperty = ['property.limitOffsetTicks'];
        properties.push(dp);
        dp = new DynProperty('property.limitOffsetTicks', BaseSettings.limitOffsetTicks, DynProperty.INTEGER, Resources.isHidden('property.limitOffsetTicks')
            ? DynProperty.HIDDEN_GROUP
            : DynProperty.TRADING_DEFAULTS_GROUP);
        dp.sortIndex = 46;
        dp.minimalValue = -NumericUtils.MAXVALUE;
        dp.maximalValue = NumericUtils.MAXVALUE;
        properties.push(dp);
        if (dc.AllowCustomSLTPTriggerPriceForUser()) {
            //  AddSLTPTriggerProperties
            properties = properties.concat(SLTPTriggerUtils.GetProperties(BaseSettings.slTpTriggers, 50, forSaving));
        }
        dp = new DynProperty('property.routing.orderType', BaseSettings.orderType, DynProperty.COMBOBOX, DynProperty.TRADING_DEFAULTS_GROUP);
        const allowedOrderTypes = TradingUtils.getAllowedOrderTypes();
        dp.objectVariants = createContextMenuItemsFromEnum(OrderType, getOrderTypeKey, (value) => {
            if (!allowedOrderTypes.Contains(value)) {
                return false;
            }
            if (!this.GetAllowedForOrderType(value)) {
                return false;
            }
            const settingsKey = `DefaultSettings.${OrderUtils.getOrderTypeLocalizationKey(value)}`;
            if (Resources.isHidden(settingsKey)) {
                return false;
            }
            const orderTypeLocalizationKey = getOrderTypeKey(value);
            if (Resources.isHidden(orderTypeLocalizationKey)) {
                return false;
            }
            return true;
        });
        dp.sortIndex = 70;
        properties.push(dp);
        dc.TIF = this.GetAllowedTifsForOrderTypes([OrderType.Market]);
        dp = new DynProperty('TIF', TerceraLineStyleComboBoxUtils.cretaComboItem(this.checkTifInArray(dc.TIF, BaseSettings.orderTif), TIF.expireTimeTIF_Market), DynProperty.COMBOBOX_COMBOITEM_TIF, DynProperty.TRADING_DEFAULTS_GROUP);
        dp.objectVariants = createContextMenuItemsFromEnum(OrderTif, getOrderTifKey, (value) => {
            return dc.TIF.Contains(value);
        });
        dp.sortIndex = 80;
        properties.push(dp);
        dc.TIF_Limit_And_Stop_Limit = this.GetAllowedTifsForOrderTypes([OrderType.Limit, OrderType.StopLimit]);
        dp = new DynProperty('TIF_Limit_And_Stop_Limit', TerceraLineStyleComboBoxUtils.cretaComboItem(this.checkTifInArray(dc.TIF_Limit_And_Stop_Limit, BaseSettings.limitAndStopLimitValidity), TIF.expireTimeTIF_Limit), DynProperty.COMBOBOX_COMBOITEM_TIF, DynProperty.TRADING_DEFAULTS_GROUP);
        dp.objectVariants = createContextMenuItemsFromEnum(OrderTif, getOrderTifKey, (value) => {
            return dc.TIF_Limit_And_Stop_Limit.Contains(value);
        });
        dp.sortIndex = 90;
        properties.push(dp);
        dc.TIF_Stop = this.GetAllowedTifsForOrderTypes([OrderType.Stop]);
        dp = new DynProperty('TIF_Stop', TerceraLineStyleComboBoxUtils.cretaComboItem(this.checkTifInArray(dc.TIF_Stop, BaseSettings.stopValidity), TIF.expireTimeTIF_Stop), DynProperty.COMBOBOX_COMBOITEM_TIF, DynProperty.TRADING_DEFAULTS_GROUP);
        dp.objectVariants = createContextMenuItemsFromEnum(OrderTif, getOrderTifKey, (value) => {
            return dc.TIF_Stop.Contains(value);
        });
        dp.sortIndex = 100;
        properties.push(dp);
        const dprtC = DataCache.productTypeEnabilityCache;
        const arrP = [];
        for (const t in dprtC) {
            arrP.push(t);
        }
        const allowedProductType = TradingUtils.getAllowedProductTypes();
        const productTypeGroup = allowedProductType.length === 1 && allowedProductType[0] === ProductType.General
            ? DynProperty.HIDDEN_GROUP
            : DynProperty.TRADING_DEFAULTS_GROUP;
        dp = new DynProperty('property.DefaultProductType', BaseSettings.productType, DynProperty.COMBOBOX, productTypeGroup);
        dp.enabled = allowedProductType.length > 1;
        dp.objectVariants = createContextMenuItemsFromEnum(ProductType, getProductTypeKey, (value) => {
            return allowedProductType.Contains(value);
        });
        dp.sortIndex = 110;
        properties.push(dp);
        const isAllowOTHTrading = DataCache.isAllowedForMyUser(RulesSet.FUNCTION_ALLOW_OTH_TRADING);
        dp = new DynProperty('property.DefaultOvernightExecution', BaseSettings.isAllowOTHTrading, DynProperty.BOOLEAN, isAllowOTHTrading ? DynProperty.TRADING_DEFAULTS_GROUP : DynProperty.HIDDEN_GROUP);
        dp.tooltip = Resources.getResource('property.DefaultOvernightExecution.descr');
        dp.sortIndex = 120;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.TYPES_MANAGER, BaseSettings.insDefSettingsStorage.GetSettingsCopy().ToXML(), DynProperty.INSTRUMENTS_DEFAULTS, DynProperty.TRADING_DEFAULTS_GROUP);
        dp.sortIndex = 130;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.CONFIRM_ORDER_PLACEMENT, BaseSettings.confirmOrderSending, DynProperty.BOOLEAN, DynProperty.CONFIRMATIONS_GROUP);
        dp.sortIndex = 1;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.CONFIRM_ORDER_CANCELLATION, BaseSettings.confirmOrderCancel, DynProperty.BOOLEAN, DynProperty.CONFIRMATIONS_GROUP);
        dp.sortIndex = 2;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.CONFIRM_ORDER_OR_POSITION_MODIFICATION, BaseSettings.confirmOrderPositionsModification, DynProperty.BOOLEAN, DynProperty.CONFIRMATIONS_GROUP);
        dp.sortIndex = 3;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.CONFIRM_POSITION_CLOSING, BaseSettings.confirmPositionClosing, DynProperty.BOOLEAN, DynProperty.CONFIRMATIONS_GROUP);
        dp.sortIndex = 4;
        properties.push(dp);
        dp = new DynProperty('property.oe.confirmReversePosition', BaseSettings.confirmReversePosition, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP);
        // https://tp.traderevolution.com/entity/98279
        // Resources.isHidden('property.oe.confirmReversePosition') ? DynProperty.HIDDEN_GROUP : DynProperty.CONFIRMATIONS_GROUP);
        dp.sortIndex = 5;
        properties.push(dp);
        dp = new DynProperty('property.oe.confirmOrderCreateAlert', BaseSettings.confirmPlaceAlert, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = 49;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.CONFIRM_ALERT_REMOVE, BaseSettings.confirmCancelAlert, DynProperty.BOOLEAN, DynProperty.CONFIRMATIONS_GROUP);
        dp.sortIndex = 49;
        properties.push(dp);
        dp = new DynProperty('property.dealTickets.openOnWorkspace', BaseSettings.openDialTicketsOnWorkspace, DynProperty.BOOLEAN, DynProperty.CONFIRMATIONS_GROUP);
        dp.sortIndex = 57;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.SHOW_MARGIN_DEAL_TICKETS_ON_WORKSPACE, BaseSettings.showMarginDealTicketOnWorkspace, DynProperty.BOOLEAN, DynProperty.CONFIRMATIONS_GROUP);
        dp.tooltip = Resources.getResource('property.dealTickets.showMarginDealTicketsOnWorkspace.ToolTip');
        dp.sortIndex = 58;
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.SHOW_DEAL_TICKETS, BaseSettings.showDialTicketsInApplicationTray, DynProperty.BOOLEAN, DynProperty.CONFIRMATIONS_GROUP);
        dp.tooltip = Resources.getResource('property.dealTickets.showDealTickets.ToolTip');
        dp.sortIndex = 59;
        properties.push(dp);
        let groupToAdd = DataCache.isAllowedForMyUser(RulesSet.FUNCTION_COPY_TRADE) && !Resources.isHidden('panel.CopyTrading')
            ? DynProperty.CONFIRMATIONS_GROUP
            : DynProperty.HIDDEN_GROUP;
        dp = new DynProperty(SessionSettingsConst.CONFIRM_ACCOUNT_FOLLOWING, BaseSettings.confirmAccountFollowing, DynProperty.BOOLEAN, groupToAdd);
        dp.sortIndex = 60;
        properties.push(dp);
        dp = new DynProperty('property.positionSizing.infoWindowShow', BaseSettings.positionSizeCalculatorInfoWindow, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP);
        properties.push(dp);
        groupToAdd =
            DataCache.isAllowedForMyUser(RulesSet.FUNCTION_PRODUCTS) && !Resources.isHidden('ribbon.tools.products')
                ? DynProperty.CONFIRMATIONS_GROUP
                : DynProperty.HIDDEN_GROUP;
        dp = new DynProperty('property.subscriptions.confirmSubscribeAndUnsubscribe', BaseSettings.confirmSubscriptions, DynProperty.BOOLEAN, groupToAdd);
        dp.sortIndex = 60;
        properties.push(dp);
        dp = new DynProperty('property.general.OvernightMarginNotificationMessage', BaseSettings.overnightMarginNotificationMessage, DynProperty.BOOLEAN, DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 2;
        properties.push(dp);
        dp = new DynProperty('property.general.WarnIfWrongOrderParam', BaseSettings.warnIfWrongOrder, DynProperty.BOOLEAN, DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 3;
        dp.tooltip = Resources.getResource('property.general.WarnIfWrongOrderParam.tooltip');
        properties.push(dp);
        dp = new DynProperty('property.general.WarnIfIdenticalOrder', new BoolNumeric(BaseSettings.warnIfIdenticalSubsequentOrderIsPlaced, BaseSettings.warnIfIdenticalOrderTime), DynProperty.BOOL_NUMERIC, Resources.isHidden('general.WarnIfOrderRepeat') ? DynProperty.HIDDEN_GROUP : DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 4;
        dp.decimalPlaces = 0;
        dp.increment = 1;
        dp.minimal = 0;
        properties.push(dp);
        groupToAdd = DataCache.getValidateMaxLotCloseOrderBoolRule()
            ? DynProperty.WARNINGS_GROUP
            : DynProperty.HIDDEN_GROUP;
        dp = new DynProperty('property.general.WarnIfQtyToCloseMoreThanMaxLot', BaseSettings.warnIfQtyToCloseMoreThanMaxLot, DynProperty.BOOLEAN, groupToAdd);
        dp.sortIndex = 5;
        properties.push(dp);
        groupToAdd = DataCache.isAllowedForMyUser(RulesSet.LEVERAGE_USAGE_WARN)
            ? DynProperty.WARNINGS_GROUP
            : DynProperty.HIDDEN_GROUP;
        dp = new DynProperty('property.general.WarnWhenEnteringIntoLeverage', BaseSettings.warnWhenEnteringIntoLeverage, DynProperty.BOOLEAN, groupToAdd);
        dp.sortIndex = 6;
        properties.push(dp);
        dp = new DynProperty('property.general.WarnIfOrderQtyExceed', new BoolNumeric(BaseSettings.warnIfOrderQtyExceed, BaseSettings.warnIfOrderQtyExceedValue), DynProperty.BOOL_NUMERIC, DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 7;
        dp.decimalPlaces = 0;
        dp.increment = 1;
        dp.minimalValue = 1;
        dp.maximalValue = 100000000;
        dp.tooltip = Resources.getResource('property.general.WarnIfOrderQtyExceed.tooltip');
        properties.push(dp);
        dp = new DynProperty('property.general.WarnIfOrderCapitalExceed', new BoolNumeric(BaseSettings.warnIfOrderCapitalExceed, BaseSettings.warnIfOrderCapitalExceedValue), DynProperty.BOOL_NUMERIC, DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 8;
        dp.decimalPlaces = 2;
        dp.increment = 0.01;
        dp.minimalValue = 1;
        dp.maximalValue = 100000000;
        dp.tooltip = Resources.getResource('property.general.WarnIfOrderCapitalExceed.tooltip');
        properties.push(dp);
        dp = new DynProperty('property.general.WarnIfTodayGrossLess', new BoolNumeric(BaseSettings.warnIfTodayGrossLess, BaseSettings.warnIfTodayGrossLessValue), DynProperty.BOOL_NUMERIC, DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 9;
        dp.decimalPlaces = 2;
        dp.increment = 0.01;
        dp.minimalValue = -100000000;
        dp.maximalValue = 100000000;
        dp.tooltip = Resources.getResource('property.general.WarnIfTodayGrossLess.tooltip');
        properties.push(dp);
        dp = new DynProperty('property.general.WarnIfAvailibleFundsLess', new BoolNumeric(BaseSettings.warnIfAvailibleFundsLess, BaseSettings.warnIfAvailibleFundsLesssValue), DynProperty.BOOL_NUMERIC, DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 10;
        dp.decimalPlaces = 2;
        dp.increment = 0.01;
        dp.minimalValue = 1;
        dp.maximalValue = 100000000;
        dp.tooltip = Resources.getResource('property.general.WarnIfAvailibleFundsLess.tooltip');
        properties.push(dp);
        dp = new DynProperty('property.general.WarnIfTodayVolumeExeed', new BoolNumeric(BaseSettings.warnIfTodayVolumeExeed, BaseSettings.warnIfTodayVolumeExeedValue), DynProperty.BOOL_NUMERIC, DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 11;
        dp.decimalPlaces = 0;
        dp.increment = 1;
        dp.minimalValue = 1;
        dp.maximalValue = 100000000;
        dp.tooltip = Resources.getResource('property.general.WarnIfTodayVolumeExeed.tooltip');
        properties.push(dp);
        dp = new DynProperty('property.general.ShowInstrumentHalt', BaseSettings.showInstrumentHalt, DynProperty.BOOLEAN, DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 12;
        dp.tooltip = Resources.getResource('property.general.ShowInstrumentHalt.tooltip');
        properties.push(dp);
        groupToAdd = DataCache.isAllowedForMyUser(RulesSet.FUNCTION_COPY_TRADE)
            ? DynProperty.WARNINGS_GROUP
            : DynProperty.HIDDEN_GROUP;
        dp = new DynProperty(SessionSettingsConst.PANEL_COPY_TRADING_WARNING_HIDING_SETTINGS_TEXT, AccountFollowerService.IsShowAccountHidingWarning, DynProperty.BOOLEAN, groupToAdd);
        dp.sortIndex = 13;
        dp.tooltip = Resources.getResource('panel.CopyTrading.Warning.Settings.Tooltip');
        properties.push(dp);
        dp = new DynProperty(SessionSettingsConst.PANEL_COPY_TRADING_WARNING_AFTER_RELOGIN_SETTINGS_TEXT, AccountFollowerService.IsShowAfterReloginWarning, DynProperty.BOOLEAN, groupToAdd);
        dp.sortIndex = 14;
        dp.tooltip = Resources.getResource('panel.CopyTrading.Warning.AfterRelogin.Settings.Tooltip');
        properties.push(dp);
        dp = new DynProperty('property.general.ShowOffMarketWarnings', BaseSettings.isShowOffMarketWarnings, DynProperty.BOOLEAN, DynProperty.WARNINGS_GROUP);
        dp.sortIndex = 15;
        dp.tooltip = Resources.getResource('property.general.ShowOffMarketWarnings.tooltip');
        properties.push(dp);
        if (!forSaving) {
            DataCache.EntitlementManager.AddNotificationProperties(properties);
        } // for Products with ProductDataType == EntitlementProductDataType.Notifications && ShowInSettings == true
        dp = new DynProperty('watchlistSheets.itemsValue', _WatchlistSheets.serialize(WatchlistSheets.items), DynProperty.STRING, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = 2;
        properties.push(dp);
        dp = new DynProperty('chartSheets.itemsValue', JSON.stringify(this.chartSheets.itemsValue), DynProperty.STRING, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = 2;
        properties.push(dp);
        dp = new DynProperty('savedOrdersSheets.itemsValue', JSON.stringify(this.savedOrdersSheets.itemsValue), DynProperty.STRING, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = 2;
        properties.push(dp);
        const curTZ = DateTimeConvertor.currentTimeZoneInfoId.value;
        properties.push(new DynProperty('property.general.timezone.current', curTZ, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        const favTZones = TimeZoneInfo.serializeToIdsString(this.favoriteTimeZoneInfoIdsSet);
        properties.push(new DynProperty('property.general.timezone.favorites', favTZones, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('property.general.Settings.TradingLock.TradingLocked', TradingLockUtils.TradingLock.tradingLocked, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('property.isSideBarCollapsed', BaseSettings.isSideBarCollapsed, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('property.appVersion', ApplicationInfo.BuildVersionData.serialize(), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('property.mobileAppQRCodeTabId', BaseSettings.mobileAppQRCodeTabId, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        // 123429
        properties = properties.concat(HotkeysManager.GetProperties());
        this.AddSeparator(properties, DynProperty.SEPARATOR_GROUP1, null);
        return properties;
    }
    applyProperties(properties) {
        SettingsLoggingManager.LogChange(this.Properties(), properties); // #111072
        const needInform = !Resources.isHidden('property.DisableInactivityPeriod');
        let needSend = false;
        if (needInform) {
            let newDIPV = null;
            const dp = DynProperty.getPropertyByName(properties, 'property.DisableInactivityPeriod');
            if (DynProperty.isCorrect(dp)) {
                newDIPV = dp.value;
            }
            needSend = newDIPV !== null && newDIPV !== BaseSettings.disableInactivityPeriod;
        }
        void this.applyNewSettingsAsync(properties).then(() => {
            ThemeManager.themeChange();
            if (needSend) {
                const msgText = BaseSettings.disableInactivityPeriod
                    ? 'Inactivity period setting was activated'
                    : 'Inactivity period setting was disabled';
                Connection.vendor.SendInactivityPeriodState(msgText);
            }
        });
    }
    AddSeparator(properties, group, sortIndex) {
        // add DynProperty of separator to properties array
        const dp = new DynProperty('', '', DynProperty.SEPARATOR, group);
        if (sortIndex !== null) {
            dp.sortIndex = sortIndex;
        }
        properties.push(dp);
    }
    GetAlertPanelProperties(needHide) {
        const properties = [];
        const Group = needHide ? DynProperty.HIDDEN_GROUP : DynProperty.ALERTS_GROUP;
        const prop1 = new DynProperty('Notification', BaseSettings.alertNotification, DynProperty.ALERT_NOTIFICATION_SELECTOR, Group);
        prop1.sortIndex = 2;
        prop1.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(prop1);
        const prop2 = new DynProperty('AlertType', BaseSettings.alertType, DynProperty.COMBOBOX, Group);
        prop2.sortIndex = 3;
        prop2.objectVariants = createContextMenuItemsFromEnum(AlertType, getAlertTypeKey);
        prop2.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(prop2);
        const prop3 = new DynProperty('Condition', BaseSettings.alertCondition, DynProperty.COMBOBOX, Group);
        prop3.sortIndex = 4;
        prop3.objectVariants = createContextMenuItemsFromEnum(AlertCondition, getAlertConditionKey);
        prop3.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(prop3);
        const prop4 = new DynProperty('Importance', BaseSettings.alertImportance, DynProperty.COMBOBOX, Group);
        prop4.sortIndex = 5;
        prop4.objectVariants = createContextMenuItemsFromEnum(AlertImportance, getAlertImportanceKey);
        prop4.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(prop4);
        const prop5 = new DynProperty('AfterExecute', BaseSettings.alertAfterExecute, DynProperty.COMBOBOX, Group);
        prop5.sortIndex = 6;
        prop5.objectVariants = createContextMenuItemsFromEnum(AlertAfterExecute, getAlertAfterExecuteKey);
        prop5.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(prop5);
        return properties;
    }
    AlertPanelCallBack(newProperties) {
        const dp1 = DynProperty.getPropertyByName(newProperties, 'Notification');
        if (dp1) {
            BaseSettings.alertNotification = dp1.value;
        }
        const dp2 = DynProperty.getPropertyByName(newProperties, 'AlertType');
        if (dp2) {
            BaseSettings.alertType = dp2.value;
        }
        const dp3 = DynProperty.getPropertyByName(newProperties, 'Condition');
        if (dp3) {
            BaseSettings.alertCondition = dp3.value;
        }
        const dp4 = DynProperty.getPropertyByName(newProperties, 'AfterExecute');
        if (dp4) {
            BaseSettings.alertAfterExecute = dp4.value;
        }
        const dp5 = DynProperty.getPropertyByName(newProperties, 'Importance');
        if (dp5) {
            BaseSettings.alertImportance = dp5.value;
        }
    }
    applyNewSettingsAsync(properties_1) {
        return __awaiter(this, arguments, void 0, function* (properties, fromJson = false, loginScreenLang = null) {
            try {
                let dp = DynProperty.getPropertyByName(properties, 'property.Language');
                if (DynProperty.isCorrect(dp)) {
                    let value = dp.value;
                    if (isValidString(loginScreenLang) && value !== loginScreenLang) {
                        // #89339
                        value = loginScreenLang;
                    }
                    BaseSettings.language = value;
                    Resources.setLocale(value);
                }
                for (let i = 0; i < this.AdditionalCallBacksHandlers.length; i++) {
                    properties = properties.concat(this.AdditionalCallBacksHandlers[i](properties));
                }
                dp = DynProperty.getPropertyByName(properties, 'ribbon.enviroment.style');
                if (DynProperty.isCorrect(dp)) {
                    if (isValidString(dp.value)) {
                        this.updateCurrentTheme(dp.value);
                    }
                }
                dp = DynProperty.getPropertyByName(properties, 'property.displayMode');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.displayMode = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.onboardinFirstTimeShowed');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.onboardInFirstTimeShowed = dp.value && !ApplicationInfo.isExploreMode;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.logSnapshotHasBeenSent');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.logSnapshotHasBeenSent = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.isCashModePreferred');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.isCashModePreferred = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.DisableInactivityPeriod');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.disableInactivityPeriod = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.DISPLAY_QUANTITY_IN_LOTS);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.isQuantityInLots = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.TOOLTIPS);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.tooltips = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.IS_ROUNDED_AVERAGE_OPEN_PRICE);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.roundedAverageOpenPrice = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.REVERSE_BUTTONS_ORDER);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.reverseButtonsOrder = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.ABBREVIATE_VOLUMES);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.abbreviateVolumes = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.Enable Sounds');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.enableSounds = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.DisableStartEndSounds');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.disableStartEndSound = dp.value;
                    BaseSettings.enableWelcomeSound = !BaseSettings.disableStartEndSound;
                    BaseSettings.enableFinishSound = !BaseSettings.disableStartEndSound;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.DOUBLE_CLICK_AVAILABILITY_FOR_POSITIONS_PANEL);
                if (DynProperty.isCorrect(dp)) {
                    IsAllowed.PositionsPanelDoubleClickAvailability = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.DOUBLE_CLICK_AVAILABILITY_FOR_ORDERS_PANEL);
                if (DynProperty.isCorrect(dp)) {
                    IsAllowed.OrdersPanelDoubleClickAvailability = dp.value;
                }
                // #region trading
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.TYPES_MANAGER);
                if (dp) {
                    const insDefSettings = new InsDefSettings();
                    insDefSettings.LoadFromXML(dp.value);
                    BaseSettings.insDefSettingsStorage.SetSettings(insDefSettings);
                }
                dp = DynProperty.getPropertyByName(properties, 'property.routing.orderType');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.orderType = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.DefaultProductType');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.productType = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.DefaultOvernightExecution');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.isAllowOTHTrading = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.SET_SLTP_VALUES_IN_OFFSET);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.isSlTpInOffset = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.SHOW_OFFSET_IN);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.offsetType = this.getAvailableOffsetMode(dp.value);
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.USE_STOP_LIMIT_INSTEAD_OF_STOP);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.useStopLimitInsteadStop = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.limitOffsetTicks');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.limitOffsetTicks = dp.value;
                }
                SLTPTriggerUtils.ApplyProperties(BaseSettings.slTpTriggers, properties, fromJson); // #109798 callBack SLTPTrigger Properties
                const dc = DataCache;
                dp = DynProperty.getPropertyByName(properties, 'TIF');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.orderTif = dp.value.tif.value;
                    TIF.expireTimeTIF_Market = TIF.handleNewExpireTime(dp.value.date);
                }
                dp = DynProperty.getPropertyByName(properties, 'TIF_Limit_And_Stop_Limit');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.limitAndStopLimitValidity = dp.value.tif.value;
                    TIF.expireTimeTIF_Limit = TIF.handleNewExpireTime(dp.value.date);
                }
                dp = DynProperty.getPropertyByName(properties, 'TIF_Stop');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.stopValidity = dp.value.tif.value;
                    TIF.expireTimeTIF_Stop = TIF.handleNewExpireTime(dp.value.date);
                }
                dp = DynProperty.getPropertyByName(properties, 'property.routing.account');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.tradingAccountID = BaseSettingsUtils.existTradingAccountIdOrFirst(dp.value);
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.defaultSymbol');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.tradingSymbolID = yield BaseSettingsUtils.existTradingSymbolIdOrFirst(dp.value);
                }
                // #endregion
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.CONFIRM_ORDER_CANCELLATION);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.confirmOrderCancel = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.CONFIRM_ORDER_PLACEMENT);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.confirmOrderSending = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.oe.confirmOrderCreateAlert');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.confirmPlaceAlert = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.CONFIRM_ALERT_REMOVE);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.confirmCancelAlert = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.CONFIRM_POSITION_CLOSING);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.confirmPositionClosing = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.oe.confirmReversePosition');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.confirmReversePosition = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.CONFIRM_ORDER_OR_POSITION_MODIFICATION);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.confirmOrderPositionsModification = dp.value;
                }
                let openDealTicketsOnWorkspaceDynPropertyAvailable = false;
                dp = DynProperty.getPropertyByName(properties, 'property.dealTickets.openOnWorkspace');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.openDialTicketsOnWorkspace = dp.value;
                    openDealTicketsOnWorkspaceDynPropertyAvailable = true;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.SHOW_MARGIN_DEAL_TICKETS_ON_WORKSPACE);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.showMarginDealTicketOnWorkspace = dp.value;
                }
                else if (openDealTicketsOnWorkspaceDynPropertyAvailable) {
                    // migration process #123386
                    BaseSettings.showMarginDealTicketOnWorkspace = Resources.isHidden('property.dealTickets.openMarginOnWorkspace.Default')
                        ? BaseSettings.openDialTicketsOnWorkspace
                        : true;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.SHOW_DEAL_TICKETS);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.showDialTicketsInApplicationTray = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.CONFIRM_ACCOUNT_FOLLOWING);
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.confirmAccountFollowing = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.positionSizing.infoWindowShow');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.positionSizeCalculatorInfoWindow = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.subscriptions.confirmSubscribeAndUnsubscribe');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.confirmSubscriptions = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.WarnIfWrongOrderParam');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.warnIfWrongOrder = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.WarnIfIdenticalOrder');
                if (DynProperty.isCorrect(dp)) {
                    const boolNumeric = dp.value;
                    if (boolNumeric) {
                        BaseSettings.warnIfIdenticalSubsequentOrderIsPlaced = boolNumeric.Checked;
                        BaseSettings.warnIfIdenticalOrderTime = boolNumeric.Value;
                    }
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.WarnIfOrderQtyExceed');
                if (DynProperty.isCorrect(dp)) {
                    const boolNumeric = dp.value;
                    if (boolNumeric) {
                        BaseSettings.warnIfOrderQtyExceed = boolNumeric.Checked;
                        BaseSettings.warnIfOrderQtyExceedValue = boolNumeric.Value;
                    }
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.WarnIfOrderCapitalExceed');
                if (DynProperty.isCorrect(dp)) {
                    const boolNumeric = dp.value;
                    if (boolNumeric) {
                        BaseSettings.warnIfOrderCapitalExceed = boolNumeric.Checked;
                        BaseSettings.warnIfOrderCapitalExceedValue = boolNumeric.Value;
                    }
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.WarnIfTodayGrossLess');
                if (DynProperty.isCorrect(dp)) {
                    const boolNumeric = dp.value;
                    if (boolNumeric) {
                        BaseSettings.warnIfTodayGrossLess = boolNumeric.Checked;
                        BaseSettings.warnIfTodayGrossLessValue = boolNumeric.Value;
                    }
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.WarnIfAvailibleFundsLess');
                if (DynProperty.isCorrect(dp)) {
                    const boolNumeric = dp.value;
                    if (boolNumeric) {
                        BaseSettings.warnIfAvailibleFundsLess = boolNumeric.Checked;
                        BaseSettings.warnIfAvailibleFundsLesssValue = boolNumeric.Value;
                    }
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.WarnIfTodayVolumeExeed');
                if (DynProperty.isCorrect(dp)) {
                    const boolNumeric = dp.value;
                    if (boolNumeric) {
                        BaseSettings.warnIfTodayVolumeExeed = boolNumeric.Checked;
                        BaseSettings.warnIfTodayVolumeExeedValue = boolNumeric.Value;
                    }
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.ShowInstrumentHalt');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.showInstrumentHalt = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.PANEL_COPY_TRADING_WARNING_HIDING_SETTINGS_TEXT);
                if (DynProperty.isCorrect(dp)) {
                    AccountFollowerService.IsShowAccountHidingWarning = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, SessionSettingsConst.PANEL_COPY_TRADING_WARNING_AFTER_RELOGIN_SETTINGS_TEXT);
                if (DynProperty.isCorrect(dp)) {
                    AccountFollowerService.IsShowAfterReloginWarning = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.WarnIfQtyToCloseMoreThanMaxLot');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.warnIfQtyToCloseMoreThanMaxLot = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.OvernightMarginNotificationMessage');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.overnightMarginNotificationMessage = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.WarnWhenEnteringIntoLeverage');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.warnWhenEnteringIntoLeverage = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.ShowOffMarketWarnings');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.isShowOffMarketWarnings = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.isSideBarCollapsed');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.isSideBarCollapsed = dp.value;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.appVersion');
                if (DynProperty.isCorrect(dp)) {
                    const appVersion = ApplicationVersion.createApplicationVersionByJson(dp.value);
                    BaseSettings.appVersion = appVersion;
                }
                dp = DynProperty.getPropertyByName(properties, 'property.mobileAppQRCodeTabId');
                if (DynProperty.isCorrect(dp)) {
                    BaseSettings.mobileAppQRCodeTabId = dp.value;
                }
                dc.EntitlementManager.callBackNotificationProperties(properties, fromJson); // #109924
                // TODO. Implement with TimeZone
                // dp = DynProperty.getPropertyByName(properties, "property.general.timezone.current");
                // if (DynProperty.isCorrect(dp))
                //    this.currentTimeZoneInfoId = dp.value;
                DateTimeConvertor.currentTimeZoneInfoId.value = TimeZoneInfo.LOCAL;
                dp = DynProperty.getPropertyByName(properties, 'property.general.timezone.favorites');
                if (DynProperty.isCorrect(dp)) {
                    this.favoriteTimeZoneInfoIdsSet = TimeZoneInfo.deserializeToIdsSet(dp.value);
                }
                dp = DynProperty.getPropertyByName(properties, 'property.general.Settings.TradingLock.TradingLocked');
                if (DynProperty.isCorrect(dp)) {
                    TradingLockUtils.TradingLock.updateTradingLock(dp.value);
                }
                dp = DynProperty.getPropertyByName(properties, 'chartSheets.itemsValue');
                if (DynProperty.isCorrect(dp)) {
                    this.chartSheets.itemsValue = JSON.parse(dp.value);
                }
                dp = DynProperty.getPropertyByName(properties, 'savedOrdersSheets.itemsValue');
                if (DynProperty.isCorrect(dp)) {
                    this.savedOrdersSheets.itemsValue = JSON.parse(dp.value);
                }
                this.AlertPanelCallBack(properties);
                // 123429
                HotkeysManager.CallBackProperties(properties);
            }
            catch (e) {
                console.log('Crashed while applying new settings. ' + e);
            }
            this.save();
        });
    }
    clear() {
        this.clearSessionSettings();
    }
    save() {
        BaseSettings.onSettingsChanged();
    }
    updateIsCashModePreferred(isCashMode) {
        if (BaseSettings.isCashModePreferred !== isCashMode) {
            BaseSettings.isCashModePreferred = isCashMode;
            this.save();
        }
    }
    updateFavoriteTimeZoneInfoIdsSet(idsSet) {
        this.favoriteTimeZoneInfoIdsSet = idsSet;
        this.save();
    }
    updateCurrentTimeZoneInfoId(currentTimeZoneInfoId) {
        DateTimeConvertor.currentTimeZoneInfoId.value = currentTimeZoneInfoId;
        this.timeZoneChanged.Raise();
        this.save();
    }
    updateCurrentTheme(currentTheme) {
        BaseSettings.currentTheme = currentTheme;
        this.save();
    }
    setSheetsData(storageName, valueOfLists, owner) {
        this[storageName].itemsValue = valueOfLists;
        this.save();
        this.sheetsChangedEvents[storageName].Raise(owner);
    }
    updateInstrumentsDefaultSettings(settings) {
        if (!settings)
            return;
        BaseSettings.insDefSettingsStorage.SetSettings(settings);
        this.save();
    }
    overnightMarginNotificationMessage(showWarning) {
        if (showWarning === undefined)
            return;
        if (BaseSettings.overnightMarginNotificationMessage !== showWarning) {
            BaseSettings.overnightMarginNotificationMessage = showWarning;
            this.save();
        }
    }
    getDefValueFromObj(defVal, ifNotObj) {
        if (defVal)
            return defVal;
        const objKeys = Object.keys(ifNotObj);
        if (objKeys.length > 0) {
            defVal = ifNotObj[objKeys[0]];
            return ifNotObj[objKeys[0]];
        }
        return null;
    }
    getFirstTradableInstrument(defValue) {
        if (defValue === null || defValue === void 0 ? void 0 : defValue.RouteIsTradable()) {
            return defValue;
        }
        const dCache = DataCache;
        const instruments = dCache.Instruments;
        for (const insId in instruments) {
            const ins = instruments[insId];
            if (ins.RouteIsTradable()) {
                return ins;
            }
        }
        return defValue;
    }
    // TODO доделать как ОЕ заменим
    static createVariantsForOrderTypesAdvanced(array) {
        const res = [];
        const len = array.length;
        const allowedKeys = DataCache.OrderParameterContainer.OrderTypes;
        for (let i = 0; i < len; i++) {
            const val = array[i];
            res.push({
                value: allowedKeys[OrderTypeMap[val]],
                text: Resources.getResource(OrderUtils.getOrderTypeLocalizationKey(val))
            });
        }
        return res;
    }
    checkTifInArray(array, type) {
        if (array.includes(type)) {
            return type;
        }
        else if (array.length > 0) {
            return array[0];
        }
        else {
            return type;
        }
    }
    // TODO. Implement Multivendor scheme.
    getDefaultTifForOrderType(ordType) {
        switch (ordType) {
            case OrderType.Market:
                return BaseSettings.orderTif;
            case OrderType.Limit:
            case OrderType.StopLimit:
                return BaseSettings.limitAndStopLimitValidity;
            case OrderType.Stop:
            case OrderType.TrailingStop:
                return BaseSettings.stopValidity;
        }
    }
    OnBoardingWasShown() {
        return BaseSettings.onboardInFirstTimeShowed;
    }
    IsCashModePreferred() {
        // #122525: If the user selected Cash, this value should be saved in the dropdown list for the next panel opening.
        return BaseSettings.isCashModePreferred;
    }
    LogSnapshotHasBeenSent() {
        // #111072
        return BaseSettings.logSnapshotHasBeenSent;
    }
    GetTriggers() {
        return BaseSettings.slTpTriggers;
    }
    onClosePropertiesScreen() {
        this._eventEmitter.emit('closePropertiesScreen');
    }
    subscribeOnClosePropertiesScreen(callback) {
        this._eventEmitter.on('closePropertiesScreen', callback);
    }
    unsubscribeOnClosePropertiesScreen(callback) {
        this._eventEmitter.off('closePropertiesScreen', callback);
    }
    requestDefaultWatchlistLists() {
        return __awaiter(this, void 0, void 0, function* () {
            const watchListItems = yield DataCache.SendDefaultAndCustomListRequest();
            const wlSheetitems = [];
            for (const watchListItem of watchListItems) {
                wlSheetitems.push(new WatchlistSheetItem(watchListItem.name, watchListItem.instrDescriptions));
            }
            return wlSheetitems;
        });
    }
    clearSessionSettings() {
        this.chartSheets = {};
        this.savedOrdersSheets = {};
    }
    isOffsetModeAvailable(value) {
        return (!Resources.isHidden(getOffsetModeViewEnumKey(value)) &&
            (value !== OffsetModeViewEnum.Percent || DataCache.IsAllowSLTPPercentForUser()));
    }
    getAvailableOffsetMode(value) {
        var _a;
        if (this.isOffsetModeAvailable(value)) {
            return value;
        }
        return ((_a = [
            OffsetModeViewEnum.Ticks,
            OffsetModeViewEnum.TicksFractionalForForex,
            OffsetModeViewEnum.Points,
            OffsetModeViewEnum.Percent
        ].find((offsetMode) => this.isOffsetModeAvailable(offsetMode))) !== null && _a !== void 0 ? _a : value);
    }
}
const ThemeNames = [ThemePreference.Dark, ThemePreference.Light, ThemePreference.System];
export const SessionSettings = new _SessionSettings();
